import { Container, Graphics, Text, TextStyle, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { setFullscreenPreference } from '@/core/fullscreen';
import { t } from '@/i18n';
import type { InputState } from '@/input/types';
import { sfx } from '@/audio/sfx';
import { chamferPanel, targetBrackets } from './widgets';

/**
 * One-time fullscreen question shown at boot (never force fullscreen).
 * YES/NO buttons: ←/→ + Space/A, or click/tap.
 */
export class FullscreenPrompt {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  open = false;
  private selected = 0; // 0 = yes, 1 = no
  private prevMoveX = 0;
  private screenW = 0;
  private screenH = 0;
  onDone: (() => void) | null = null;

  constructor() {
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer);
  }

  show(screenW: number, screenH: number): void {
    this.open = true;
    this.view.visible = true;
    this.screenW = screenW;
    this.screenH = screenH;
    this.rebuild();
  }

  private rebuild(): void {
    const { screenW, screenH } = this;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: 0.9 });
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));

    const title = new Text({
      text: t('fs.title'),
      style: new TextStyle({
        fontFamily: 'Tektur, Arial Black, sans-serif', fontSize: 34, fill: 0xffffff, letterSpacing: 2,
      }),
    });
    title.anchor.set(0.5);
    title.position.set(screenW / 2, screenH * 0.4);
    this.layer.addChild(title);

    const labels = [t('fs.yes'), t('fs.no')];
    labels.forEach((label, i) => {
      const selected = i === this.selected;
      const btn = new Container();
      const bg = new Graphics();
      chamferPanel(bg, 220, 60, {
        fill: selected ? 0x2a2440 : 0x161222,
        stroke: selected ? COLORS.uiAccent : 0x4a4256,
        strokeWidth: selected ? 4 : 3,
        notch: 14,
      });
      bg.position.set(-110, -30);
      if (selected) targetBrackets(bg, 220, 60);
      const txt = new Text({
        text: label,
        style: new TextStyle({
          fontFamily: 'Tektur, Arial Black, sans-serif', fontSize: 24,
          fill: selected ? COLORS.playerYellow : 0xffffff, letterSpacing: 3,
        }),
      });
      txt.anchor.set(0.5);
      btn.addChild(bg, txt);
      btn.position.set(screenW / 2 + (i === 0 ? -140 : 140), screenH * 0.55);
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.on('pointerover', (e: FederatedPointerEvent) => {
        if (e.pointerType === 'mouse' && this.selected !== i) {
          this.selected = i;
          this.rebuild();
        }
      });
      btn.on('pointertap', () => this.choose(i === 0));
      this.layer.addChild(btn);
    });
  }

  private choose(yes: boolean): void {
    sfx.uiConfirm();
    setFullscreenPreference(yes);
    this.open = false;
    this.view.visible = false;
    this.onDone?.();
  }

  /** @returns true while consuming input */
  handleInput(input: InputState): boolean {
    if (!this.open) return false;
    const edge = Math.abs(input.moveX) > 0.5 && Math.abs(this.prevMoveX) <= 0.5
      ? Math.sign(input.moveX) : 0;
    this.prevMoveX = input.moveX;
    if (edge !== 0) {
      this.selected = this.selected === 0 ? 1 : 0;
      sfx.uiMove();
      this.rebuild();
    }
    if (input.dashPressed) this.choose(this.selected === 0);
    return true;
  }
}
